import { css, type Theme } from "@emotion/react";
import {
  buildFormValidate,
  minLength,
  required,
  validEmail,
} from "@turo/libraries/lib/helpers/validationHelpers";
import { recaptcha } from "@turo/libraries/lib/recaptcha/recaptcha";
import { CheckboxControl, PrimaryButton, TextInput } from "@turo/pedal";
import { StyledText } from "@turo/ui";
import { type FC, type FormEventHandler, useEffect } from "react";

import ButtonLink from "~/src/components/ButtonLink";
import { onShowSignupFormStep } from "~/src/helpers/formEventActions";
import { useSimpleTranslate } from "~/src/SimpleTranslateProvider";
import type { StateFormField } from "~/types/stateForm";

import stateForm, { FIELD_TYPES } from "../../components/stateForm";
import config from "../../config";
import {
  getPropertiesFromQuery,
  type GetPropertiesQueryResults,
  post,
} from "../../helpers/iframeHelpers";
import TextMessage from "../../text/TextMessage";
import type { SignupTranslations } from ".";
import { fetchProperties, guessCountryForSignup } from "./EmailSignupHelpers";

/**
 * In order to open a page from an iframe, we need to rely on
 * window.open
 */
const buildOpenPage = (page: string) => (e: Event) => {
  e.stopPropagation();
  window.open(page);
};

const openPolicyPage = buildOpenPage("/policies/privacy");
const openTermsPage = buildOpenPage("/policies/terms");

export interface SignupErrorResponse {
  error: string;
  errors?: Array<{ message: string }>;
}

export interface SignupSuccessResponse {
  [key: string]: unknown;
  success?: boolean;
}

interface EmailSignupFormComponentProps {
  hideMarketingSection: GetPropertiesQueryResults["hideMarketingSection"];
  isOptinMatrixEnabled: GetPropertiesQueryResults["isOptinMatrixEnabled"];
  marketingSmsOptIn: GetPropertiesQueryResults["marketingSmsOptIn"];
  /**
   * Whether the account name clarity feature is enabled or not.
   * If enabled, the user will see different text/caption for first/last name fields
   */
  onEmailAlreadyUsed: (email: string) => Promise<void>;
  otpTokenLoginStart: () => void;
  partnerReservationId: GetPropertiesQueryResults["partnerReservationId"];
  referrer: GetPropertiesQueryResults["referrer"];
  signupFailure: (data: SignupErrorResponse) => void;
  signupStart: () => void;
  signupSuccess: (data: SignupSuccessResponse) => void;
}

interface StateFormProps {
  buildSubmitHandler: (
    onSubmit: (formData: {
      email: string;
      firstName: string;
      lastName: string;
      marketing: boolean;
      password: string;
      smsMarketing: boolean;
      tos: boolean;
    }) => Promise<unknown>,
  ) => FormEventHandler<HTMLFormElement>;
  formFields: {
    email: StateFormField;
    firstName: StateFormField;
    lastName: StateFormField;
    marketing: StateFormField;
    password: StateFormField;
    smsMarketing: StateFormField;
    tos: StateFormField;
  };
  isLoading: boolean;
}

type EmailSignupFormProps = EmailSignupFormComponentProps & StateFormProps;

const formStyles = (theme: Theme) => css`
  padding: 0 ${theme.pedal.spacing.space_4};
  overflow: hidden;
  text-align: left;
`;

const nameRowStyles = (theme: Theme) => css`
  display: grid;
  gap: ${theme.pedal.spacing.space_16};
  margin-bottom: ${theme.pedal.spacing.space_16};
`;

const innerDiv = css`
  width: 100%;
`;

const passwordStyles = (theme: Theme) => css`
  margin: ${theme.pedal.spacing.space_16} 0;
`;

const checkboxWithLabelStyles = css`
  display: flex;
  align-items: center;
`;

const textWithPointerCursor = css`
  cursor: pointer;
`;

/**
 * Implements the recaptcha and submit logic for the signup form.
 */
function EmailSignupForm({
  buildSubmitHandler,
  formFields,
  hideMarketingSection,
  isLoading,
  isOptinMatrixEnabled,
  marketingSmsOptIn,
  onEmailAlreadyUsed,
  partnerReservationId,
  referrer,
  signupFailure,
  signupStart,
  signupSuccess,
}: EmailSignupFormProps) {
  const translate = useSimpleTranslate<SignupTranslations>();

  useEffect(() => {
    // Initialize recaptcha
    void recaptcha.initialize(config.GOOGLE_CAPTCHA_SITE_KEY);

    // Call guess_country and properties parallelly
    const fetchData = async () => {
      try {
        const [country, properties] = await Promise.all([
          guessCountryForSignup(),
          fetchProperties(),
        ]);

        const checkboxMarketingOptInPrecheckedForCountries =
          properties?.checkboxMarketingOptInPrecheckedForCountries || [];

        if (
          checkboxMarketingOptInPrecheckedForCountries.includes(country) &&
          !hideMarketingSection
        ) {
          /**
           * Sets marketing field checkbox state initial value, which we cannot set outside of
           * the component (like the other `initialValues`) due to needing access to API data.
           */
          // @ts-expect-error -- TODO-TS: Convert to `onChange` event handler
          formFields.marketing.onChange({ target: { checked: true } });
        }
      } catch {
        // TODO: swallow error, let it be uncaught by the browser, or other handling?
      }
    };

    void fetchData();
    // only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onShowSignupFormStep();
  }, []);

  const handleSubmit = buildSubmitHandler(
    ({
      email,
      firstName,
      lastName,
      marketing,
      password,
      smsMarketing,
      tos,
    }) => {
      signupStart();
      return recaptcha
        .execute(recaptcha.ACTIONS.SIGNUP, config.GOOGLE_CAPTCHA_SITE_KEY)
        .catch((error) => {
          signupFailure({ error: "error_generic" });
          throw error;
        })
        .then((token) =>
          post<SignupSuccessResponse, SignupErrorResponse>(
            "/api/signup",
            {
              _signedUpForMarketingCommunication: "on",
              _termsOfServiceAccepted: "on",
              email,
              firstName,
              lastName,
              password,
              partnerReservationId: partnerReservationId || "",
              referrer: referrer || window.location.pathname,
              signedUpForMarketingCommunication: marketing,
              signedUpForSmsMarketingCommunication: smsMarketing,
              termsOfServiceAccepted: tos,
            },
            { [recaptcha.HEADER_NAME]: token },
          ).then(async (data) => {
            // Type guard to check if data has error property
            if ("error" in data) {
              if (data.error === "email_in_use") {
                try {
                  // we need to let the parent component know that the email is already in use
                  // so that it can show the confirm OTP form
                  // and we don't want to fire the signupFailure callback
                  await onEmailAlreadyUsed(email);
                } catch (error) {
                  console.error("Failed to handle email already used:", error);
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  signupFailure(data as unknown as SignupErrorResponse);
                }
                return;
              }
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              signupFailure(data as unknown as SignupErrorResponse);
            } else {
              signupSuccess(data);
            }
          }),
        );
    },
  );

  return (
    <form
      css={formStyles}
      data-testid="emailSignupForm"
      noValidate
      onSubmit={handleSubmit}
    >
      <div css={nameRowStyles}>
        <div css={innerDiv}>
          <TextInput
            data-testid="firstName"
            id="firstName"
            label={translate(
              "auth.email_signup.first_name_on_driver_license.label",
            )}
            name="firstName"
            onBlur={formFields.firstName.onBlur}
            onChange={formFields.firstName.onChange}
            status={formFields.firstName.error ? "error" : undefined}
            supportingText={translate("auth.email_signup.first_name_caption")}
            // If an error occurs, the default supporting text above will be replaced by the error message.
            {...(formFields.firstName.error
              ? {
                  supportingText: translate(
                    "auth.email_signup.firstname.required",
                  ),
                }
              : undefined)}
            type={FIELD_TYPES.TEXT}
            value={formFields.firstName.value}
          />
        </div>
        <div css={innerDiv}>
          <TextInput
            data-testid="lastName"
            id="lastName"
            label={translate(
              "auth.email_signup.last_name_on_driver_license.label",
            )}
            name="lastName"
            onBlur={formFields.lastName.onBlur}
            onChange={formFields.lastName.onChange}
            status={formFields.lastName.error ? "error" : undefined}
            supportingText={
              formFields.lastName.error &&
              translate("auth.email_signup.lastname.required")
            }
            type={FIELD_TYPES.TEXT}
            value={formFields.lastName.value}
          />
        </div>
      </div>

      <TextInput
        data-testid="email"
        id="email"
        label={translate("auth.email_signup.email.label")}
        name="email"
        onBlur={formFields.email.onBlur}
        onChange={formFields.email.onChange}
        status={formFields.email.error ? "error" : undefined}
        supportingText={
          formFields.email.error &&
          translate("auth.email_signup.email.error_format")
        }
        type={FIELD_TYPES.EMAIL}
        value={formFields.email.value}
      />
      <div css={passwordStyles}>
        <TextInput
          data-testid="password"
          id="password"
          label={translate("auth.email_signup.password.label")}
          name="password"
          onBlur={formFields.password.onBlur}
          onChange={formFields.password.onChange}
          status={formFields.password.error ? "error" : undefined}
          supportingText={
            formFields.password.error &&
            translate("auth.email_signup.password.error_min_length")
          }
          type={FIELD_TYPES.PASSWORD}
          value={formFields.password.value}
        />
      </div>

      {!hideMarketingSection && (
        <label css={[checkboxWithLabelStyles, passwordStyles]}>
          <CheckboxControl
            checked={!!formFields.marketing.value}
            data-testid="marketing-checkbox"
            id="marketing"
            name="marketing"
            onChange={formFields.marketing.onChange}
            variantRole="neutral"
          />
          <StyledText
            color="text_01"
            css={textWithPointerCursor}
            marginBottom={0}
            marginLeft={8}
            styleName="caption"
            tag="span"
          >
            {translate(
              isOptinMatrixEnabled
                ? "auth.email_signup.marketing.optin_matrix_label"
                : "auth.email_signup.marketing.label",
            )}
          </StyledText>
        </label>
      )}

      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label css={[checkboxWithLabelStyles, passwordStyles]}>
        <CheckboxControl
          checked={!!formFields.tos.value}
          data-status={formFields.tos.error ? "error" : undefined}
          data-testid="tos"
          id="tos"
          name="tos"
          onChange={formFields.tos.onChange}
          status={formFields.tos.error ? "error" : undefined}
          variantRole="neutral"
        />
        <StyledText
          color={formFields.tos.error ? "text_error" : "text_01"}
          css={textWithPointerCursor}
          marginBottom={0}
          marginLeft={8}
          styleName="caption"
          tag="span"
        >
          <TextMessage
            message="auth.email_signup.terms_of_service.label"
            params={{
              privacyPolicyLink: (text: string) => (
                <ButtonLink key="privacy" onClick={openPolicyPage}>
                  {text}
                </ButtonLink>
              ),
              termsOfServiceLink: (text: string) => (
                <ButtonLink key="tos" onClick={openTermsPage}>
                  {text}
                </ButtonLink>
              ),
            }}
          />
        </StyledText>
      </label>

      {!hideMarketingSection && marketingSmsOptIn && (
        <label css={[checkboxWithLabelStyles, passwordStyles]}>
          <CheckboxControl
            checked={!!formFields.smsMarketing.value}
            data-testid="sms-marketing-checkbox"
            id="smsMarketing"
            name="smsMarketing"
            onChange={formFields.smsMarketing.onChange}
            variantRole="neutral"
          />
          <StyledText
            color="text_01"
            css={textWithPointerCursor}
            marginBottom={0}
            marginLeft={8}
            styleName="caption"
            tag="span"
          >
            {translate("auth.email_signup.sms_marketing.label")}
          </StyledText>
        </label>
      )}

      <PrimaryButton
        data-testid="submitSignupButton"
        isLoading={isLoading}
        type="submit"
      >
        <TextMessage message="auth.email_signup.sign_up" />
      </PrimaryButton>
    </form>
  );
}

const { email, firstName, lastName } = getPropertiesFromQuery() || {};
const initialValues = {
  firstName,
  lastName,
  email,
};

/**
 * Adds field state and form validations to the signup form
 */
// @ts-expect-error -- TODO-TS: Convert stateForm
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast is necessary until we convert stateForm to TypeScript
export default stateForm(EmailSignupForm, {
  initialValues,
  types: {
    email: FIELD_TYPES.EMAIL,
    firstName: FIELD_TYPES.TEXT,
    lastName: FIELD_TYPES.TEXT,
    marketing: FIELD_TYPES.CHECKBOX,
    password: FIELD_TYPES.PASSWORD,
    smsMarketing: FIELD_TYPES.CHECKBOX,
    tos: FIELD_TYPES.CHECKBOX,
  },
  validate: buildFormValidate({
    email: validEmail(),
    firstName: required(),
    lastName: required(),
    password: minLength(8),
    tos: required(),
  }),
}) as FC<EmailSignupFormComponentProps>;
